import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './MessageWindow.css';
import { useNavigate, useParams } from 'react-router-dom';
import InfoPanel from './InfoPanel';
import { useTranslation } from 'react-i18next';
import { 
    FaPaperPlane, 
    FaLightbulb, 
    FaCheck, 
    FaTimes, 
    FaLanguage, 
    FaTruck, 
    FaUser, 
    FaRobot, 
    FaEllipsisV, 
    FaEdit,
    FaFile,
    FaTimesCircle,
    FaInfoCircle,
    FaBox,
    FaShippingFast,
    FaCheckCircle,
    FaBarcode,
    FaFilePdf,
    FaFileImage,
    FaFileAlt,
    FaFileArchive,
    FaFileCode,
    FaTag,
    FaBuilding,
    FaMapMarkerAlt,
    FaCity,
    FaGlobe,
    FaPhone,
    FaEnvelope,
    FaIdCard,
    FaInfo,
    FaMoneyBillWave,
    FaBoxOpen,
    FaShieldAlt,
    FaReceipt,
    FaComments,
    FaHandPaper,
    FaExclamationTriangle,
    FaUserPlus,
    FaCircleNotch,
    FaStickyNote,
    FaPlus,
    FaThumbsUp,
    FaThumbsDown,
    FaCommentDots,
    FaWarehouse,
    FaArrowLeft
} from 'react-icons/fa';
import WMSChat from './WMSChat';
import CarriersConversation from './CarriersConversation';
import ManualConversation from './ManualConversation';
import WarehouseConversation from './WarehouseConversation';

const MessageWindow = () => {
    const { t } = useTranslation();
    const { ticketId } = useParams();
    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [employee, setEmployee] = useState('');
    const [newMessage, setNewMessage] = useState('');
    const [editingMessage, setEditingMessage] = useState(null);
    const [editedContent, setEditedContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [userLoading, setUserLoading] = useState(true);
    const [targetLanguage, setTargetLanguage] = useState('original');
    const [translatedMessages, setTranslatedMessages] = useState({});
    const [customerLoading, setCustomerLoading] = useState(false);
    const [orderLoading, setOrderLoading] = useState(false);
    const navigate = useNavigate();
    const [overlayState, setOverlayState] = useState({ show: false, message: '', success: false });
    const [user, setUser] = useState(null);
    const [clientInfo, setClientInfo] = useState('');
    const [orderDetails, setOrderDetails] = useState('');
    const [summary, setSummary] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);
    const [carriersConversation, setCarriersConversation] = useState(null);
    const [continuationMessage, setContinuationMessage] = useState('');
    const [testUserId, setTestUserId] = useState('');
    const [testThreadId, setTestThreadId] = useState('');
    const [testAssistantId, setTestAssistantId] = useState('');
    const [validatingMessage, setValidatingMessage] = useState(false);
    const [activeTab, setActiveTab] = useState('carriers');
    const [showActionMenu, setShowActionMenu] = useState(false);
    const [showLanguageMenu, setShowLanguageMenu] = useState(false);
    const actionMenuRef = useRef(null);

    // Add missing state variables for employee assignment
    const [employees, setEmployees] = useState([]);
    const [showAssignmentDropdown, setShowAssignmentDropdown] = useState(false);
    const [assigningTicket, setAssigningTicket] = useState(false);
    const assignmentDropdownRef = useRef(null);

    // State for ticket files
    const [ticketFiles, setTicketFiles] = useState([]);
    const [filesLoading, setFilesLoading] = useState(true);
    // State for file overlay
    const [selectedFile, setSelectedFile] = useState(null);

    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const pingIntervalRef = useRef(null);

    // Add new state for context data
    const [contextData, setContextData] = useState(null);
    const [contextLoading, setContextLoading] = useState(true);

    // Add new state for warehouse email form
    const [warehouseEmailForm, setWarehouseEmailForm] = useState({
        subject: '',
        content: '',
        recipient_email: ''
    });

    // Add new state for niveau2 email form
    const [niveau2EmailForm, setNiveau2EmailForm] = useState({
        subject: '',
        content: '',
        recipient_email: '',
        selectedFiles: []
    });

    // Add new state for file selection
    const [selectedTicketFiles, setSelectedTicketFiles] = useState([]);

    // Add new state for warehouse conversation
    const [warehouseConversation, setWarehouseConversation] = useState(null);
    const [warehouseLoading, setWarehouseLoading] = useState(true);

    // Add new state for file upload functionality
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const fileInputRef = useRef(null);

    // Add new state for notes functionality
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [noteContent, setNoteContent] = useState('');
    const [selectedMessageForNote, setSelectedMessageForNote] = useState(null);
    const [addingNote, setAddingNote] = useState(false);

    // Add new state for AI help loading
    const [gettingAiHelp, setGettingAiHelp] = useState(false);

    // Add new state for tracking which draft message is being sent
    const [sendingDraftMessageId, setSendingDraftMessageId] = useState(null);

    // Add new state for AI feedback functionality
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [feedbackContent, setFeedbackContent] = useState('');
    const [selectedMessageForFeedback, setSelectedMessageForFeedback] = useState(null);
    const [submittingFeedback, setSubmittingFeedback] = useState(false);

    // Add new state for the two-step feedback process
    const [feedbackStep, setFeedbackStep] = useState('input'); // 'input' | 'preview' | 'applying'
    const [proposedModifications, setProposedModifications] = useState([]);
    const [selectedModifications, setSelectedModifications] = useState([]);
    const [modificationSummary, setModificationSummary] = useState('');
    const [assistantInfo, setAssistantInfo] = useState(null);

    // Add new state for message expansion
    const [isMessageExpanded, setIsMessageExpanded] = useState(false);

    const fetchTicketContext = async () => {
        setContextLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/${ticketId}/context`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setContextData(response.data);
        } catch (error) {
        } finally {
            setContextLoading(false);
        }
    };

    const fetchCarriersConversation = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/carriers_conversation/${ticketId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCarriersConversation(response.data);
        } catch (error) {
        }
    };

    const fetchWarehouseConversation = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/warehouse_conversation/${ticketId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setWarehouseConversation(response.data);
        } catch (error) {
        } finally {
            setWarehouseLoading(false);
        }
    };

    const connectWebSocket = () => {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        wsRef.current = new WebSocket(`/api/ws?token=${token}`);
        
        wsRef.current.onopen = (event) => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        wsRef.current.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        wsRef.current.onclose = (event) => {
            reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
        };

        wsRef.current.onmessage = (event) => {
            if (event.data === 'ping') {
                wsRef.current.send('pong');
            } else {
                // Fetch messages only to quickly update the message list
                fetchMessages();
            }
        };
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
                setShowActionMenu(false);
                setShowLanguageMenu(false);
            }
            if (assignmentDropdownRef.current && !assignmentDropdownRef.current.contains(event.target)) {
                setShowAssignmentDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const initialLoad = async () => {
            await fetchUserData(); // Fetches and sets user
            // Other non-ticket specific initial fetches can go here if any
        };
        initialLoad();

        // WebSocket and ping interval setup will be handled by the useEffect below, dependent on 'user'
        
        return () => {
            // General cleanup for the component if any, not specific to WebSocket anymore here
        };
    }, []); // Runs once on component mount

    // New useEffect to handle WebSocket connection based on user
    useEffect(() => {
        if (user) {
            connectWebSocket();
            pingIntervalRef.current = setInterval(() => {
                if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                    wsRef.current.send('ping');
                }
            }, 30000);
        }

        return () => {
            // Cleanup WebSocket and ping interval if they were initialized
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [user]); // Runs when 'user' state changes

    // Add new useEffect to fetch employees when user is available
    useEffect(() => {
        if (user) {
            fetchEmployees();
        }
    }, [user]);

    // This useEffect will run when ticketId changes, or when the user state is updated
    // (e.g., after fetchUserData completes).
    useEffect(() => {
        if (user !== undefined && ticketId) { // user can be null (failed login) or an object
            fetchTicketAndMessages();
        }
    }, [ticketId, user]);

    const fetchUserData = async () => {
        setUserLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                 setUser(null); 
                 setUserLoading(false);
                 navigate('/'); 
                 return;
            }
            const response = await axios.get('/api/users/user', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
        } catch (error) {
            setUser(null); 
            if (error.response && (error.response.status === 401 || error.response.status === 422)) {
                localStorage.removeItem('access_token');
                navigate('/');
            }
        } finally {
            setUserLoading(false);
        }
    };

    const fetchTicketAndMessages = async () => {
        setLoading(true);
        const token = localStorage.getItem('access_token');

        try {
            if (!token) {
                setTicket(null);
                setMessages([]);
                setLoading(false);
                navigate('/');
                return;
            }

            const response = await axios.get(`/api/tickets/${ticketId}/messages`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data && response.data.ticket) {
                setTicket(response.data.ticket);
                setMessages(response.data.messages || []);
                setEmployee(response.data.employee || 'N/A');
                
                fetchOrderAndCustomerInfo(response.data.ticket, token);
                
                setSummary(response.data.ticket.summary || response.data.ticket.subject || 'No summary available.');
                
                await fetchTicketContext();
                await fetchCarriersConversation();
                // Fetch warehouse conversation for edn_api_account users
                if (user?.edn_api_account) {
                    await fetchWarehouseConversation();
                }
                await fetchTicketFiles();
            } else {
                setTicket(null);
                setMessages([]);
            }
            setLoading(false);
            
        } catch (error) {
            setTicket(null); 
            setMessages([]);
            setLoading(false);
        }
    };

    const fetchMessages = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/${ticketId}/messages`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setMessages(response.data.messages);
        } catch (error) {
        }
    };

    const fetchOrderAndCustomerInfo = async (ticketData, token) => {
        if (ticketData.customer_id) {
            fetchCustomerInfo(ticketData.customer_id, token);
        } else if (ticketData.customer_email) {
            setClientInfo(`Email: ${ticketData.customer_email}`);
        } else {
            setClientInfo('');
        }

        if (ticketData.order) {
            fetchOrderInfo(ticketData.order, token);
        } else {
            setOrderDetails('');
        }
    };

    // New function to fetch ticket files
    const fetchTicketFiles = async () => {
        setFilesLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/${ticketId}/files`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setTicketFiles(response.data.files);
        } catch (error) {
        } finally {
            setFilesLoading(false);
        }
    };

    const formatMessageContent = (content) => {
        const lines = content.split('\n');
        return lines.map((line, index) => {
            // Regular expression to match URLs ending with .pdf
            const urlRegex = /(https?:\/\/[^\s]+\.pdf)/gi;
            const parts = line.split(urlRegex);
            
            return (
                <span key={index}>
                    {parts.map((part, partIndex) => {
                        if (part.match(urlRegex)) {
                            // Convert http to https
                            const secureUrl = part.replace('http://', 'https://');
                            return (
                                <div key={partIndex}>
                                    <object
                                        data={secureUrl}
                                        type="application/pdf"
                                        width="100%"
                                        height="500px"
                                    >
                                        <p>Unable to display PDF. <a href={secureUrl}>Download PDF</a></p>
                                    </object>
                                </div>
                            );
                        }
                        return part;
                    })}
                    <br />
                </span>
            );
        });
    };
    
    const fetchCustomerInfo = async (customerId, token) => {
        setCustomerLoading(true);
        try {
            const response = await axios.get(`/api/shopify/get_customer`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { customer_id: customerId }
            });
            const customer = response.data;
            setClientInfo(`Phone: ${customer.phone}\nFirst Name: ${customer.first_name}\nLast Name: ${customer.last_name}\nEmail: ${customer.email}`);
        } catch (error) {
        } finally {
            setCustomerLoading(false);
        }
    };

    const fetchOrderInfo = async (orderId, token) => {
        setOrderLoading(true);
        try {
            const response = await axios.get(`/api/logistics/edn/order_by_id/${orderId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const order = response.data;
            const formattedOrderDetails = `
Order ID: ${order.order_id}
Reference: ${order.reference}
Status: ${order.status}
Total Price: ${order.grand_total} ${order.currency}
Shipping Cost: ${order.shipping_cost} ${order.currency}
Created At: ${order.created_at}
Customer Email: ${order.customer_email}
Customer Name: ${order.customer_name}
Customer Phone: ${order.customer_phone}
Shipping Address:
    ${order.shipping_address.firstname} ${order.shipping_address.lastname}
    ${order.shipping_address.street1}
    ${order.shipping_address.street2 ? order.shipping_address.street2 + '\n' : ''}
    ${order.shipping_address.postcode} ${order.shipping_address.city}
    ${order.shipping_address.country}
Line Items:
${order.items.map(item => `
Product ID: ${item.product_id}
Name: ${item.name}
SKU: ${item.sku}
Quantity: ${item.quantity}
Price: ${item.price} ${order.currency}
EAN: ${item.ean || 'N/A'}
`).join('\n')}
`;
            setOrderDetails(formattedOrderDetails);
        } catch (error) {
        } finally {
            setOrderLoading(false);
        }
    };

    const handleSendMessage = async () => {
        try {
            const token = localStorage.getItem('access_token');
            await axios.post('/api/messages/add', {
                ticket_id: ticketId,
                content: newMessage,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setNewMessage('');
            fetchMessages(); // Fetch messages only to update the message list quickly
        } catch (error) {
        }
    };

    const handleGetAssistantResponse = async () => {
        setGettingAiHelp(true);
        try {
            const token = localStorage.getItem('access_token');
            const aiLogsString = JSON.stringify(ticket.ai_logs);

            const response = await axios.post('/api/messages/writer_assistant', {
                text: newMessage,
                ai_logs: aiLogsString,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setNewMessage(response.data.response);
        } catch (error) {
        } finally {
            setGettingAiHelp(false);
        }
    };

    const handleResolveTicket = async () => {
        setOverlayState({ show: true, message: t('messageWindow.processing'), success: false });
        try {
            const token = localStorage.getItem('access_token');
            await axios.put(`/api/tickets/${ticketId}/resolve`, null, {
                headers: { Authorization: `Bearer ${token}` },
            });
            await fetchTicketAndMessages(); // Fetch ticket and messages to update the status and messages
            setOverlayState({ show: true, message: t('messageWindow.resolveSuccess'), success: true });
        } catch (error) {
            setOverlayState({ show: true, message: t('messageWindow.resolveError'), success: false });
        } finally {
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        }
    };
    
    const handleCloseTicket = async () => {
        setOverlayState({ show: true, message: t('messageWindow.processing'), success: false });
        try {
            const token = localStorage.getItem('access_token');
            await axios.put(`/api/tickets/${ticketId}/close`, null, {
                headers: { Authorization: `Bearer ${token}` },
            });
            await fetchTicketAndMessages(); // Fetch ticket and messages to update the status and messages
            setOverlayState({ show: true, message: t('messageWindow.closeSuccess'), success: true });
        } catch (error) {
            setOverlayState({ show: true, message: t('messageWindow.closeError'), success: false });
        } finally {
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        }
    };

    const handleSendDraftMessage = async (messageId) => {
        setSendingDraftMessageId(messageId);
        try {
            const token = localStorage.getItem('access_token');
            await axios.post(`/api/messages/send`, { message_id: messageId }, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            fetchMessages(); // Fetch messages to update the message list
        } catch (error) {
            // Optionally show an error message to the user
            setOverlayState({ 
                show: true, 
                message: t('messageWindow.sendError', 'Failed to send message'), 
                success: false 
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        } finally {
            setSendingDraftMessageId(null);
        }
    };

    const handleDeleteDraftMessage = async (messageId) => {
        try {
            const token = localStorage.getItem('access_token');
            await axios.post(`/api/messages/delete`, { message_id: messageId }, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            
            // Immediately remove the message from local state for better UX
            setMessages(prevMessages => prevMessages.filter(message => message._id !== messageId));
        } catch (error) {
            // Optionally show an error message to the user
            setOverlayState({ 
                show: true, 
                message: t('messageWindow.deleteError', 'Failed to delete message'), 
                success: false 
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        }
    };

    const handleModifyDraftMessage = (message) => {
        setEditingMessage(message);
        setEditedContent(message.content);
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const handleSaveModifiedMessage = async () => {
        try {
            const token = localStorage.getItem('access_token');
            await axios.post(`/api/messages/edit`, {
                message_id: editingMessage._id,
                content: editedContent,
            }, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            setEditingMessage(null);
            setEditedContent('');
            fetchMessages(); // Fetch messages to update the message list
        } catch (error) {
        }
    };

    const handleCancelModification = () => {
        setEditingMessage(null);
        setEditedContent('');
    };

    // File overlay functions
    const handleFileClick = (file) => {
        setSelectedFile(file);
    };

    const closeFileOverlay = () => {
        setSelectedFile(null);
    };

    const fetchEmployees = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get('/api/employees/agents/basic', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setEmployees(response.data);
        } catch (error) {
        }
    };

    const handleAssignTicket = async (employeeId) => {
        if (!ticket.thread_id) {
            setOverlayState({ 
                show: true, 
                message: t('messageWindow.assignmentError'), 
                success: false 
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
            return;
        }

        setAssigningTicket(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.post('/api/employees/assign-employee', {
                employee_id: employeeId,
                thread_id: ticket.thread_id
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            
            setOverlayState({ 
                show: true, 
                message: t('messageWindow.assignmentSuccess'), 
                success: true 
            });
            setShowAssignmentDropdown(false);
            
            // Refresh ticket data to show updated assignment
            await fetchTicketAndMessages();
        } catch (error) {
            setOverlayState({ 
                show: true, 
                message: t('messageWindow.assignmentError'), 
                success: false 
            });
        } finally {
            setAssigningTicket(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        }
    };

    useEffect(() => {
        if (!userLoading && !user) {
            navigate('/');
        }
    }, [userLoading, user, navigate]);

    if (loading) {
        return <div>{t('messageWindow.loading')}</div>;
    }

    if (!ticket) {
        return <div>{t('messageWindow.failedToLoadTicket')}</div>;
    }

    const isEditable = !['resolved', 'closed', 'ai_resolved'].includes(ticket.status);
    
    const handleLanguageChange = async (language) => {
        setTargetLanguage(language);
        if (language !== 'original') {
            setIsTranslating(true);
            await translateMessages(language);
            setIsTranslating(false);
        } else {
            setTranslatedMessages({});
        }
    };

    const translateMessages = async (language) => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.post('/api/messages/translate_messages', {
                messages: messages.map(m => ({ id: m._id, content: m.content })),
                target_language: language
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const translatedObj = {};
            response.data.translated_messages.forEach(m => {
                translatedObj[m.id] = m.content;
            });
            setTranslatedMessages(translatedObj);
        } catch (error) {
        }
    };

    const renderMessageContent = (message) => {
        const content = targetLanguage === 'original' ? message.content : (translatedMessages[message._id] || message.content);
        return formatMessageContent(content);
    };

    // Updated AI action rendering to show result status instead of function name
    const renderAiAction = (message) => {
        const isAction = message.direction === 'is_action';
        const isActionResult = message.direction === 'is_action_result';
        
        return (
            <div key={message._id} className={`ai-action ${isAction ? 'action' : 'action-result'}`}>
                <div className="ai-action-content">
                    {isAction && message.function_call && (
                        <span className="ai-function-call">
                            🔧 Calling: {message.function_call.name}
                        </span>
                    )}
                    {isActionResult && (
                        <span className="ai-function-result">
                            {(() => {
                                try {
                                    // Parse the content to extract status
                                    const result = JSON.parse(message.content.replace(/'/g, '"'));
                                    const status = result.status || 'completed';
                                    const icon = status === 'success' ? '✅' : status === 'error' ? '❌' : '⚡';
                                    return `${icon} ${status}`;
                                } catch (e) {
                                    // Fallback if parsing fails
                                    return `✅ completed`;
                                }
                            })()}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    // Add new function to render note cards
    const renderNoteCard = (message) => {
        if (!message.notes || message.notes.length === 0) return null;

        return (
            <div key={`notes-${message._id}`} className="note-card">
                <div className="note-card-header">
                    <FaStickyNote />
                    <span className="note-card-title">
                        {t('messageWindow.notes')} ({message.notes.length})
                    </span>
                </div>
                <div className="note-card-content">
                    {message.notes.map((note, index) => (
                        <div key={index} className="note-item">
                            <div className="note-content">{note.content}</div>
                            <div className="note-meta">
                                <span className="note-date">{formatNoteDate(note.added_at)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderMessage = (message) => {
        // Check if this is an AI action
        if (message.direction === 'is_action' || message.direction === 'is_action_result') {
            return renderAiAction(message);
        }

        // Regular message rendering
        const isSent = message.sender === 'employee' || message.status === 'sent' || message.status === 'draft';
        const messageClass = `message ${isSent ? 'sent' : 'received'} ${message.status}`;
        
        // Check if this message is being edited
        const isBeingEdited = editingMessage && editingMessage._id === message._id;
        
        // Check if message has notes
        const hasNotes = message.notes && message.notes.length > 0;
        
        return (
            <div key={message._id} className={messageClass}>
                {message.status === 'automated' && (
                    <span className="message-status-badge automated">{t('messageWindow.automated')}</span>
                )}
                
                {isBeingEdited ? (
                    // Edit mode UI
                    <div className="message-edit-container">
                        <textarea
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                            className="message-edit-textarea"
                            rows={4}
                            placeholder={t('messageWindow.editMessage')}
                        />
                        <div className="edit-actions">
                            <button 
                                className="edit-action-button save" 
                                onClick={handleSaveModifiedMessage}
                            >
                                <FaCheck /> {t('messageWindow.save')}
                            </button>
                            <button 
                                className="edit-action-button cancel" 
                                onClick={handleCancelModification}
                            >
                                <FaTimes /> {t('messageWindow.cancel')}
                            </button>
                        </div>
                    </div>
                ) : (
                    // Normal message display
                    <div className="message-content">
                        {renderMessageContent(message)}
                    </div>
                )}
                
                <div className="message-meta">
                    <span className="message-status">
                        {message.status 
                            ? t(`messageWindow.statusOptions.${message.status}`)
                            : t('messageWindow.statusOptions.received')}
                    </span>
                    <span className="message-timestamp">{formatDate(message.created_at)}</span>
                    
                    <div className="message-actions">
                        {/* Add note button and indicator */}
                        {user && user.role !== 'experience' && (
                            <div className="message-note-actions">
                                <button 
                                    className={`note-button ${hasNotes ? 'has-notes' : ''}`}
                                    onClick={() => openNoteModal(message)}
                                    title={t('messageWindow.addNote')}
                                >
                                    <FaStickyNote />
                                    {hasNotes && <span className="note-count">{message.notes.length}</span>}
                                </button>
                            </div>
                        )}

                        {/* Add feedback button for automated (AI-generated) messages */}
                        {user && user.role !== 'experience' && message.status === 'draft' && (
                            <div className="message-feedback-actions">
                                <button 
                                    className="feedback-button"
                                    onClick={() => openFeedbackModal(message)}
                                    title={t('messageWindow.improveFeedback')}
                                >
                                    <FaCommentDots />
                                    <span className="feedback-text">{t('messageWindow.feedback')}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                
                {message.status === 'draft' && isEditable && !isBeingEdited && (
                    <div className="draft-actions">
                        <button 
                            className="draft-message-button send" 
                            onClick={() => handleSendDraftMessage(message._id)}
                            disabled={sendingDraftMessageId === message._id}
                        >
                            {sendingDraftMessageId === message._id ? (
                                <>
                                    <FaCircleNotch className="spinner" /> {t('messageWindow.sending')}
                                </>
                            ) : (
                                <>
                                    <FaPaperPlane /> {t('messageWindow.send')}
                                </>
                            )}
                        </button>
                        <button className="draft-message-button delete" onClick={() => handleDeleteDraftMessage(message._id)}>
                            <FaTimes /> {t('messageWindow.delete')}
                        </button>
                        <button className="draft-message-button modify" onClick={() => handleModifyDraftMessage(message)}>
                            <FaEdit /> {t('messageWindow.modify')}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const handleValidateCarrierMessage = async (conversationId, approved) => {
        setValidatingMessage(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.post('/api/logistics/validate-carrier-message', 
                {
                    conversation_id: conversationId,
                    approved: approved
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            
            // Refresh the carriers conversation after validation
            await fetchCarriersConversation();
            
            setOverlayState({ 
                show: true, 
                message: approved ? t('messageWindow.messageApproved') : t('messageWindow.messageRejected'), 
                success: true 
            });
        } catch (error) {
            setOverlayState({ 
                show: true, 
                message: t('messageWindow.validationError'), 
                success: false 
            });
        } finally {
            setValidatingMessage(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        }
    };

    const getFileIcon = (mimeType) => {
        if (mimeType.includes('pdf')) {
            return <FaFilePdf />;
        } else if (mimeType.includes('image')) {
            return <FaFileImage />;
        } else if (mimeType.includes('zip') || mimeType.includes('rar')) {
            return <FaFileArchive />;
        } else if (mimeType.includes('code') || mimeType.includes('json')) {
            return <FaFileCode />;
        }
        return <FaFileAlt />;
    };

    const getFileClass = (mimeType) => {
        if (mimeType.includes('pdf')) {
            return 'pdf';
        } else if (mimeType.includes('image')) {
            return 'image';
        }
        return 'document';
    };

    // Add this new component for the metadata tooltip
    const FileMetadataTooltip = ({ metadata }) => {
        if (!metadata || Object.keys(metadata).length === 0) return null;
        
        return (
            <div className="file-metadata-content">
                {Object.entries(metadata).map(([key, value]) => (
                    value && (
                        <div key={key} className={`metadata-item metadata-${key}`}>
                            <strong>{t(`messageWindow.metadata.${key}`, key)}:</strong> {value}
                        </div>
                    )
                ))}
            </div>
        );
    };

    // Add file processing function
    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                // Remove the data:image/jpeg;base64, prefix
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = error => reject(error);
        });
    };

    // Handle file selection and immediate upload
    const handleFileSelection = async (event) => {
        const files = Array.from(event.target.files);
        
        if (files.length === 0) return;
        
        // Validate file count (max 10 files)
        if (files.length > 10) {
            setOverlayState({
                show: true,
                message: t('messageWindow.maxFilesError'),
                success: false
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
            return;
        }

        // Validate total file size (max 50MB total)
        const totalSize = files.reduce((sum, file) => sum + file.size, 0);
        const maxSize = 50 * 1024 * 1024; // 50MB
        
        if (totalSize > maxSize) {
            setOverlayState({
                show: true,
                message: t('messageWindow.fileSizeError'),
                success: false
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
            return;
        }

        setUploadingFiles(true);
        
        try {
            const processedFiles = [];

            // Process all files to base64
            for (const file of files) {
                const base64Content = await convertFileToBase64(file);
                processedFiles.push({
                    name: file.name,
                    mime_type: file.type,
                    content: base64Content,
                    size: file.size
                });
            }

            // Upload files immediately
            const token = localStorage.getItem('access_token');
            const response = await axios.post(
                `/api/tickets/${ticketId}/files`,
                { files: processedFiles },
                {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                }
            );

            // Clear file input and refresh ticket files
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            
            // Refresh the ticket files list
            await fetchTicketFiles();
            
            setOverlayState({
                show: true,
                message: t('messageWindow.filesUploadedSuccess'),
                success: true
            });
        } catch (error) {
            setOverlayState({
                show: true,
                message: t('messageWindow.fileUploadError'),
                success: false
            });
        } finally {
            setUploadingFiles(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
        }
    };

    // Add API function for adding notes
    const handleAddNote = async () => {
        if (!noteContent.trim() || !selectedMessageForNote) return;

        setAddingNote(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.post('/api/messages/add_note', {
                message_id: selectedMessageForNote._id,
                note: noteContent.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Refresh messages to show the new note
            await fetchMessages();
            
            // Reset note form
            setNoteContent('');
            setSelectedMessageForNote(null);
            setShowNoteModal(false);
            
            setOverlayState({
                show: true,
                message: t('messageWindow.noteAddedSuccess'),
                success: true
            });
        } catch (error) {
            setOverlayState({
                show: true,
                message: t('messageWindow.noteAddError'),
                success: false
            });
        } finally {
            setAddingNote(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        }
    };

    // Function to open note modal
    const openNoteModal = (message) => {
        setSelectedMessageForNote(message);
        setNoteContent('');
        setShowNoteModal(true);
    };

    // Function to close note modal
    const closeNoteModal = () => {
        setShowNoteModal(false);
        setNoteContent('');
        setSelectedMessageForNote(null);
    };

    // Function to format note date
    const formatNoteDate = (dateString) => {
        const options = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    // Updated API function for submitting feedback with two-step process
    const handleSubmitFeedback = async () => {
        if (!feedbackContent.trim() || !selectedMessageForFeedback) return;

        setSubmittingFeedback(true);
        try {
            const token = localStorage.getItem('access_token');
            
            // Step 1: Get preview of proposed modifications
            const response = await axios.post('/api/ai-assistants/improve-instructions', {
                ticket_id: ticketId,
                message_id: selectedMessageForFeedback._id,
                comment: feedbackContent.trim(),
                action: 'preview'
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Debug logging
            console.log('API Response:', response.data);
            console.log('Modifications:', response.data.modifications);

            // Store the proposed modifications and switch to preview step
            setProposedModifications(response.data.modifications || []);
            setSelectedModifications(response.data.modifications?.map((_, index) => index) || []); // Select all by default
            setModificationSummary(response.data.modification_summary || '');
            setAssistantInfo({
                id: response.data.assistant_id,
                name: response.data.assistant_name,
                currentInstructionsPreview: response.data.current_instructions_preview
            });
            setFeedbackStep('preview');
            
        } catch (error) {
            console.error('Feedback submission error:', error);
            setOverlayState({
                show: true,
                message: t('messageWindow.feedbackSubmitError'),
                success: false
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
        } finally {
            setSubmittingFeedback(false);
        }
    };

    // Function to handle modification selection
    const handleModificationToggle = (index) => {
        setSelectedModifications(prev => {
            if (prev.includes(index)) {
                return prev.filter(i => i !== index);
            } else {
                return [...prev, index];
            }
        });
    };

    // Function to apply selected modifications
    const handleApplyModifications = async () => {
        if (selectedModifications.length === 0) {
            setOverlayState({
                show: true,
                message: t('messageWindow.noModificationsSelected'),
                success: false
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
            return;
        }

        setFeedbackStep('applying');
        try {
            const token = localStorage.getItem('access_token');
            
            // Get the approved modifications based on selected indices
            const approvedModifications = selectedModifications.map(index => proposedModifications[index]);
            
            // Step 2: Apply the approved modifications
            const response = await axios.post('/api/ai-assistants/improve-instructions', {
                ticket_id: ticketId,
                message_id: selectedMessageForFeedback._id,
                comment: feedbackContent.trim(),
                action: 'apply',
                approved_modifications: approvedModifications
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Reset feedback form and close modal
            setFeedbackContent('');
            setSelectedMessageForFeedback(null);
            setShowFeedbackModal(false);
            setFeedbackStep('input');
            setProposedModifications([]);
            setSelectedModifications([]);
            setModificationSummary('');
            setAssistantInfo(null);
            
            setOverlayState({
                show: true,
                message: t('messageWindow.modificationsAppliedSuccess', {
                    count: approvedModifications.length,
                    assistant: response.data.assistant_name
                }),
                success: true
            });
        } catch (error) {
            setOverlayState({
                show: true,
                message: t('messageWindow.applyModificationsError'),
                success: false
            });
            setFeedbackStep('preview'); // Go back to preview step
        } finally {
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
        }
    };

    // Function to go back to input step
    const handleBackToInput = () => {
        setFeedbackStep('input');
        setProposedModifications([]);
        setSelectedModifications([]);
        setModificationSummary('');
        setAssistantInfo(null);
    };

    // Function to open feedback modal
    const openFeedbackModal = (message) => {
        setSelectedMessageForFeedback(message);
        setFeedbackContent('');
        setShowFeedbackModal(true);
        setFeedbackStep('input');
        setProposedModifications([]);
        setSelectedModifications([]);
        setModificationSummary('');
        setAssistantInfo(null);
        setIsMessageExpanded(false); // Reset expansion state
    };

    // Updated function to close feedback modal
    const closeFeedbackModal = () => {
        setShowFeedbackModal(false);
        setFeedbackContent('');
        setSelectedMessageForFeedback(null);
        setFeedbackStep('input');
        setProposedModifications([]);
        setSelectedModifications([]);
        setModificationSummary('');
        setAssistantInfo(null);
        setIsMessageExpanded(false); // Reset expansion state
    };

    return (
        <div className="app-container">
            <div className="message-window-layout">
                <div className="ticket-header">
                    <div className="ticket-title-row">
                        <div className="ticket-title-container">
                            <h2>{ticket.subject}</h2>
                        </div>
                        <div className="header-actions">
                            {ticketFiles.length > 0 && (
                                <div className="ticket-file-indicators">
                                    {ticketFiles.map((file, index) => (
                                        <div
                                            key={index}
                                            className={`ticket-file-indicator ${getFileClass(file.mime_type)}`}
                                            onClick={() => handleFileClick(file)}
                                        >
                                            {getFileIcon(file.mime_type)}
                                            <div className="ticket-file-tooltip">
                                                <p className="tooltip-filename">{file.name}</p>
                                                {file.metadata && Object.keys(file.metadata).length > 0 && (
                                                    <FileMetadataTooltip metadata={file.metadata} />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* Add assignment dropdown */}
                            {user && user.role !== 'experience' && isEditable && (
                                <div className="assignment-dropdown-container" ref={assignmentDropdownRef}>
                                    <button 
                                        className="assignment-trigger"
                                        onClick={() => setShowAssignmentDropdown(!showAssignmentDropdown)}
                                        disabled={assigningTicket}
                                        title={t('messageWindow.assignToEmployee')}
                                    >
                                        <FaUserPlus />
                                    </button>
                                    {showAssignmentDropdown && (
                                        <div className="assignment-dropdown">
                                            <div className="assignment-dropdown-header">
                                                {t('messageWindow.assignToEmployee')}
                                            </div>
                                            {employees.length > 0 ? (
                                                <div className="assignment-dropdown-list">
                                                    {employees.map((employee) => (
                                                        <button
                                                            key={employee._id}
                                                            className="assignment-dropdown-item"
                                                            onClick={() => handleAssignTicket(employee._id)}
                                                            disabled={assigningTicket}
                                                        >
                                                            {employee.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="assignment-dropdown-empty">
                                                    {t('messageWindow.noEmployeesAvailable')}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            <div className="action-menu-container" ref={actionMenuRef}>
                                <button 
                                    className="action-menu-trigger"
                                    onClick={() => setShowActionMenu(!showActionMenu)}
                                >
                                    <FaEllipsisV />
                                </button>
                                {showActionMenu && (
                                    <div className="action-menu">
                                        {isEditable && (
                                            <>
                                                <button onClick={handleResolveTicket}>
                                                    <FaCheck /> {t('messageWindow.resolveTicket')}
                                                </button>
                                                <button onClick={handleCloseTicket}>
                                                    <FaTimes /> {t('messageWindow.closeTicket')}
                                                </button>
                                            </>
                                        )}
                                        <div className="action-menu-item" 
                                             onClick={() => setShowLanguageMenu(!showLanguageMenu)}>
                                            <span>
                                                <FaLanguage /> {t('messageWindow.translate')}
                                            </span>
                                            {showLanguageMenu && (
                                                <div className="language-submenu">
                                                    <button onClick={() => {
                                                        handleLanguageChange('original');
                                                        setShowLanguageMenu(false);
                                                    }}>
                                                        {t('messageWindow.original')}
                                                    </button>
                                                    <button onClick={() => {
                                                        handleLanguageChange('en');
                                                        setShowLanguageMenu(false);
                                                    }}>English</button>
                                                    <button onClick={() => {
                                                        handleLanguageChange('fr');
                                                        setShowLanguageMenu(false);
                                                    }}>Français</button>
                                                    <button onClick={() => {
                                                        handleLanguageChange('es');
                                                        setShowLanguageMenu(false);
                                                    }}>Español</button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="ticket-meta">
                        <span className={`status-badge ${ticket.status}`}>
                            {t(`messageWindow.statusOptions.${ticket.status}`, ticket.status)}
                        </span>
                        <span className="ticket-employee">
                            {t('messageWindow.employee')}: {employee}
                        </span>
                        <div className="ticket-indicators">
                            {contextData && (
                                <>
                                    <div className="ticket-indicator tracking" title={t('messageWindow.masterTracking')}>
                                        <FaBarcode />
                                        <span>{contextData.shipping_info.master_tracking || t('messageWindow.noTracking')}</span>
                                    </div>
                                    {contextData.shipping_info.external_ticket_id && (
                                        <div className="ticket-indicator tracking" title={t('messageWindow.externalTicketId', 'External Ticket ID')}>
                                            <FaIdCard />
                                            <span>{contextData.shipping_info.external_ticket_id}</span>
                                        </div>
                                    )}
                                    <div className="ticket-indicator tracking" title={t('messageWindow.topic')}>
                                        <FaTag />
                                        <span>{contextData.shipping_info.topic || t('messageWindow.noTopic')}</span>
                                    </div>
                                    <div className="ticket-indicator client" title={t('messageWindow.clientName')}>
                                        <FaUser />
                                        <span>{contextData.client_info.name || t('messageWindow.noClient')}</span>
                                    </div>
                                    <div className="ticket-indicator carrier" title={t('messageWindow.carrierName')}>
                                        <FaTruck />
                                        <span>{contextData.shipping_info.carrier_name || t('messageWindow.noCarrier')}</span>
                                    </div>
                                    <div className={`ticket-indicator status ${contextData.shipping_info.is_shipped ? '' : 'off'}`} 
                                         title={t('messageWindow.shipped')}>
                                        <FaShippingFast />
                                        <span>{t('messageWindow.shipped')}</span>
                                    </div>
                                    <div className={`ticket-indicator status ${contextData.shipping_info.is_delivered ? '' : 'off'}`}
                                         title={t('messageWindow.delivered')}>
                                        <FaBox />
                                        <span>{t('messageWindow.delivered')}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                
                <div className="message-window-content">
                    <div className="message-window-messages">
                        {messages.map((message) => (
                            <React.Fragment key={`fragment-${message._id}`}>
                                {renderMessage(message)}
                                {renderNoteCard(message)}
                            </React.Fragment>
                        ))}
                    </div>

                    {isEditable && (
                        <div className="input-container">
                            <textarea
                                value={newMessage}
                                onChange={(e) => {
                                    setNewMessage(e.target.value);
                                    // Auto-grow functionality
                                    e.target.style.height = '40px';
                                    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                                }}
                                placeholder={t('messageWindow.typeYourResponse')}
                                className="message-window-textarea"
                                rows={1}
                            />
                            <div className="response-actions">
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileSelection}
                                    multiple
                                    accept="*/*"
                                    style={{ display: 'none' }}
                                />
                                <button 
                                    className="message-window-action-button message-window-attach"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploadingFiles}
                                    title={t('messageWindow.attachFiles')}
                                >
                                    {uploadingFiles ? (
                                        <>
                                            <FaCircleNotch className="spinner" /> {t('messageWindow.uploading')}
                                        </>
                                    ) : (
                                        <>
                                            <FaFile /> {t('messageWindow.attachFiles')}
                                        </>
                                    )}
                                </button>
                                <button className="message-window-action-button message-window-send" onClick={handleSendMessage}>
                                    <FaPaperPlane /> {t('messageWindow.send')}
                                </button>
                                <button 
                                    className="message-window-action-button message-window-ai-help" 
                                    onClick={handleGetAssistantResponse}
                                    disabled={gettingAiHelp}
                                >
                                    {gettingAiHelp ? (
                                        <>
                                            <FaCircleNotch className="spinner" /> {t('messageWindow.gettingAiHelp')}
                                        </>
                                    ) : (
                                        <>
                                            <FaLightbulb /> {t('messageWindow.aiHelp')}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="side-panel">
                <div className="tab-buttons">
                    <button 
                        className={`tab-button ${activeTab === 'carriers' ? 'active' : ''}`}
                        onClick={() => setActiveTab('carriers')}
                        title={t('messageWindow.carriersConversation')}
                    >
                        <FaComments />
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'manual' ? 'active' : ''}`}
                        onClick={() => setActiveTab('manual')}
                        title={t('messageWindow.manualConversation')}
                    >
                        <FaHandPaper />
                    </button>
                    {/* Add warehouse tab for edn_api_account users */}
                    {user && user.edn_api_account && (
                        <button 
                            className={`tab-button ${activeTab === 'warehouse' ? 'active' : ''}`}
                            onClick={() => setActiveTab('warehouse')}
                            title={t('messageWindow.warehouseConversation')}
                        >
                            <FaWarehouse />
                        </button>
                    )}
                    <button 
                        className={`tab-button ${activeTab === 'user' ? 'active' : ''}`}
                        onClick={() => setActiveTab('user')}
                        title={t('messageWindow.userInfo')}
                    >
                        <FaUser />
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'track' ? 'active' : ''}`}
                        onClick={() => setActiveTab('track')}
                        title={t('messageWindow.trackingInfo')}
                    >
                        <FaShippingFast />
                    </button>
                </div>
                
                <div className="tab-content">
                    {activeTab === 'carriers' && (
                        <CarriersConversation 
                            conversation={carriersConversation} 
                            onValidateMessage={handleValidateCarrierMessage}
                            isValidating={validatingMessage}
                            onRefresh={fetchCarriersConversation}
                        />
                    )}
                    {activeTab === 'manual' && (
                        <ManualConversation 
                            onRefresh={fetchCarriersConversation}
                            ticketFiles={ticketFiles}
                            ticket={ticket}
                        />
                    )}
                    {/* Add warehouse tab content for edn_api_account users */}
                    {activeTab === 'warehouse' && user && user.edn_api_account && (
                        <WarehouseConversation 
                            ticketId={ticketId}
                            onRefresh={fetchWarehouseConversation}
                        />
                    )}
                    {activeTab === 'user' && contextData && (
                        <div className="user-tab">
                            <div className="user-section">
                                <h3>{t('messageWindow.recipientInfo')}</h3>
                                {contextData.recipient_info.company && (
                                    <p><FaBuilding /><strong>{t('messageWindow.company')}:</strong> {contextData.recipient_info.company}</p>
                                )}
                                <p><FaUser /><strong>{t('messageWindow.name')}:</strong> {contextData.recipient_info.name}</p>
                                <h4>{t('messageWindow.address')}</h4>
                                <p><FaMapMarkerAlt /><strong>{t('messageWindow.street')}:</strong> {contextData.recipient_info.address.street1}</p>
                                {contextData.recipient_info.address.street2 && 
                                    <p><FaMapMarkerAlt /><strong>{t('messageWindow.street2')}:</strong> {contextData.recipient_info.address.street2}</p>
                                }
                                <p><FaCity /><strong>{t('messageWindow.city')}:</strong> {contextData.recipient_info.address.postcode} {contextData.recipient_info.address.city}</p>
                                <p><FaGlobe /><strong>{t('messageWindow.country')}:</strong> {contextData.recipient_info.address.country}</p>
                                <h4>{t('messageWindow.contact')}</h4>
                                <p><FaPhone /><strong>{t('messageWindow.phone')}:</strong> {contextData.recipient_info.contact.phone}</p>
                                <p><FaEnvelope /><strong>{t('messageWindow.email')}:</strong> {contextData.recipient_info.contact.email}</p>
                                {contextData.shipping_info.label_url && (
                                    <p><strong>{t('messageWindow.labelUrl')}:</strong> 
                                        <a href={contextData.shipping_info.label_url} target="_blank" rel="noopener noreferrer">
                                            {t('messageWindow.viewLabel')}
                                        </a>
                                    </p>
                                )}
                            </div>

                            <div className="user-section">
                                <h3>{t('messageWindow.clientInfo')}</h3>
                                <p><FaGlobe /><strong>{t('messageWindow.client_email')}:</strong> {contextData.client_info.email_client}</p>
                                <p><FaIdCard /><strong>{t('messageWindow.clientId')}:</strong> {contextData.client_info.id}</p>
                                <p><FaUser /><strong>{t('messageWindow.clientName')}:</strong> {contextData.client_info.name}</p>
                                <p><FaInfo /><strong>{t('messageWindow.clientStatus')}:</strong> {contextData.client_info.status}</p>
                            </div>

                            <div className="user-section">
                                <h3>{t('messageWindow.packageInfo')}</h3>
                                <p><FaBarcode /><strong>{t('messageWindow.reference')}:</strong> {contextData.order_info.reference}</p>
                                <div className="package-content">
                                    <FaBox />
                                    <p>{t('messageWindow.packageContent')}:</p>
                                    <div className="package-content-truncated">
                                        {contextData.order_info.package_content}
                                    </div>
                                    <div className="package-content-full">
                                        {contextData.order_info.package_content}
                                    </div>
                                </div>
                                <p><FaMoneyBillWave /><strong>{t('messageWindow.goodsValue')}:</strong> {contextData.order_info.goods_value} {contextData.order_info.currency}</p>
                                <p><FaBoxOpen /><strong>{t('messageWindow.packageValue')}:</strong> {contextData.order_info.package_value} {contextData.order_info.currency}</p>
                                <p><FaShieldAlt /><strong>{t('messageWindow.insuranceValue')}:</strong> {contextData.order_info.insurance_value} {contextData.order_info.currency}</p>
                                {contextData.order_info.ht_price !== null && (
                                    <p><FaReceipt /><strong>{t('messageWindow.htPrice')}:</strong> {contextData.order_info.ht_price} {contextData.order_info.currency}</p>
                                )}
                            </div>

                            {contextData.compatibility_result && (
                                <div className="user-section">
                                    <h3><FaExclamationTriangle /> {t('messageWindow.compatibilityResult', 'Compatibility Result')}</h3>
                                    <div className="compatibility-result-content">
                                        {Object.entries(contextData.compatibility_result).map(([key, value]) => {
                                            if (value === null || value === undefined || value === '') return null;
                                            
                                            // Special handling for parts array
                                            if (key === 'parts' && Array.isArray(value)) {
                                                return (
                                                    <div key={key} className="compatibility-parts">
                                                        <p><strong>{t('messageWindow.compatibility.parts', 'Parts')}:</strong></p>
                                                        <div className="parts-list">
                                                            {value.map((part, index) => (
                                                                <div key={index} className="part-item">
                                                                    <div className="part-details">
                                                                        <p className="part-group">
                                                                            <strong>{t('messageWindow.compatibility.partGroup', 'Part Group')}:</strong> {part.part_group}
                                                                        </p>
                                                                        <p className="part-number">
                                                                            <strong>{t('messageWindow.compatibility.partNumber', 'Part Number')}:</strong> {part.part_number}
                                                                        </p>
                                                                        {part.part_name && (
                                                                            <p className="part-name">
                                                                                <strong>{t('messageWindow.compatibility.partName', 'Part Name')}:</strong> {part.part_name}
                                                                            </p>
                                                                        )}
                                                                        {part.url && (
                                                                            <p className="part-url">
                                                                                <strong>{t('messageWindow.compatibility.partUrl', 'Link')}:</strong>{' '}
                                                                                <a 
                                                                                    href={part.url} 
                                                                                    target="_blank" 
                                                                                    rel="noopener noreferrer"
                                                                                    className="part-link"
                                                                                >
                                                                                    {t('messageWindow.compatibility.viewPart', 'View Part')}
                                                                                </a>
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            
                                            // Handle different value types for other fields
                                            let displayValue = value;
                                            if (typeof value === 'boolean') {
                                                displayValue = value ? t('messageWindow.yes', 'Yes') : t('messageWindow.no', 'No');
                                            } else if (typeof value === 'object') {
                                                displayValue = JSON.stringify(value, null, 2);
                                            }
                                            
                                            return (
                                                <p key={key}>
                                                    <strong>{t(`messageWindow.compatibility.${key}`, key.replace(/_/g, ' '))}:</strong> {displayValue}
                                                </p>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {activeTab === 'track' && contextData && (
                        <div className="track-tab">
                            <div className="tracking-section">
                                <h3>{t('messageWindow.shippingInfo')}</h3>
                                
                                {contextData.shipping_info.tracking_info && (
                                    <div className="tracking-info">
                                        <p><strong>{t('messageWindow.currentStatus')}:</strong> {contextData.shipping_info.tracking_info.status}</p>
                                        
                                        {contextData.shipping_info.tracking_info.events && 
                                         contextData.shipping_info.tracking_info.events.length > 0 && (
                                            <>
                                                <h4>{t('messageWindow.trackingHistory')}</h4>
                                                <div className="tracking-events">
                                                    {contextData.shipping_info.tracking_info.events.map((event, index) => (
                                                        <div key={index} className="tracking-event">
                                                            <div className="event-date">
                                                                {new Date(event.date).toLocaleString()}
                                                            </div>
                                                            {event.location && (
                                                                <div className="event-location">
                                                                    {event.location}
                                                                </div>
                                                            )}
                                                            <div className="event-status">
                                                                {event.status}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Note Modal */}
            {showNoteModal && (
                <div className="note-modal-overlay">
                    <div className="note-modal">
                        <div className="note-modal-header">
                            <h3>{t('messageWindow.addNote')}</h3>
                            <button className="note-modal-close" onClick={closeNoteModal}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="note-modal-content">
                            <div className="note-message-preview">
                                <p><strong>{t('messageWindow.messagePreview')}:</strong></p>
                                <div className="preview-content">
                                    {selectedMessageForNote?.content.substring(0, 100)}
                                    {selectedMessageForNote?.content.length > 100 && '...'}
                                </div>
                            </div>
                            <textarea
                                value={noteContent}
                                onChange={(e) => setNoteContent(e.target.value)}
                                placeholder={t('messageWindow.noteContentPlaceholder')}
                                className="note-textarea"
                                rows={4}
                                maxLength={500}
                            />
                            <div className="note-char-count">
                                {noteContent.length}/500
                            </div>
                        </div>
                        <div className="note-modal-actions">
                            <button 
                                className="note-cancel-button" 
                                onClick={closeNoteModal}
                                disabled={addingNote}
                            >
                                {t('messageWindow.cancel')}
                            </button>
                            <button 
                                className="note-save-button" 
                                onClick={handleAddNote}
                                disabled={!noteContent.trim() || addingNote}
                            >
                                {addingNote ? (
                                    <>
                                        <FaCircleNotch className="spinner" /> {t('messageWindow.adding')}
                                    </>
                                ) : (
                                    <>
                                        <FaPlus /> {t('messageWindow.addNote')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Updated AI Feedback Modal */}
            {showFeedbackModal && (
                <div className="feedback-modal-overlay">
                    <div className="feedback-modal">
                        <div className="feedback-modal-header">
                            <h3>
                                {feedbackStep === 'input' && t('messageWindow.improveAiResponse')}
                                {feedbackStep === 'preview' && t('messageWindow.reviewProposedChanges')}
                                {feedbackStep === 'applying' && t('messageWindow.applyingChanges')}
                            </h3>
                            <button className="feedback-modal-close" onClick={closeFeedbackModal}>
                                <FaTimes />
                            </button>
                        </div>
                        
                        <div className="feedback-modal-content">
                            {feedbackStep === 'input' && (
                                <>
                                    <div className="feedback-message-preview">
                                        <p><strong>{t('messageWindow.aiMessagePreview', 'AI Message Preview')}:</strong></p>
                                        <div className="preview-content ai-message">
                                            {/* Remove the AI badge */}
                                            <div className="message-content-preview">
                                                {isMessageExpanded 
                                                    ? selectedMessageForFeedback?.content
                                                    : `${selectedMessageForFeedback?.content.substring(0, 200)}${selectedMessageForFeedback?.content.length > 200 ? '...' : ''}`
                                                }
                                            </div>
                                            {selectedMessageForFeedback?.content.length > 200 && (
                                                <button 
                                                    className="expand-message-button"
                                                    onClick={() => setIsMessageExpanded(!isMessageExpanded)}
                                                    type="button"
                                                >
                                                    {isMessageExpanded 
                                                        ? t('messageWindow.showLess', 'Show less') 
                                                        : t('messageWindow.showMore', 'Show more')
                                                    }
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="feedback-instructions">
                                        <p>{t('messageWindow.feedbackInstructions', 'Describe what should be improved in this AI response. Be specific about what was wrong or what could be better.')}</p>
                                    </div>
                                    <textarea
                                        value={feedbackContent}
                                        onChange={(e) => setFeedbackContent(e.target.value)}
                                        placeholder={t('messageWindow.feedbackPlaceholder', 'Example: The response should be more concise, or it should include specific information about...')}
                                        className="feedback-textarea"
                                        rows={4}
                                        maxLength={1000}
                                    />
                                    <div className="feedback-char-count">
                                        {feedbackContent.length}/1000
                                    </div>
                                </>
                            )}

                            {feedbackStep === 'preview' && (
                                <>
                                    <div className="modification-preview-header">
                                        <div className="assistant-info">
                                            <p><strong>{t('messageWindow.assistant', 'Assistant')}:</strong> {assistantInfo?.name}</p>
                                            <p><strong>{t('messageWindow.modificationSummary', 'Summary')}:</strong> {modificationSummary}</p>
                                        </div>
                                        <div className="current-instructions-preview">
                                            <p><strong>{t('messageWindow.currentInstructions', 'Current Instructions')}:</strong></p>
                                            <div className="instructions-preview">
                                                {assistantInfo?.currentInstructionsPreview}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="modifications-list">
                                        <h4>{t('messageWindow.proposedModifications', 'Proposed Changes')} ({proposedModifications.length})</h4>
                                        <p className="modifications-instruction">
                                            {t('messageWindow.selectModificationsToApply', 'Review and select the changes you want to apply:')}
                                        </p>
                                        
                                        {proposedModifications.length === 0 ? (
                                            <div className="no-modifications">
                                                <p>{t('messageWindow.noModificationsGenerated', 'No changes were suggested based on your feedback.')}</p>
                                            </div>
                                        ) : (
                                            proposedModifications.map((modification, index) => {
                                                // Create user-friendly display
                                                const getModificationTitle = (mod) => {
                                                    switch(mod.type) {
                                                        case 'replace': return t('messageWindow.modificationType.replace', 'Update existing rule');
                                                        case 'add_after': return t('messageWindow.modificationType.add', 'Add new rule');
                                                        case 'add_before': return t('messageWindow.modificationType.addBefore', 'Insert rule before');
                                                        case 'remove': return t('messageWindow.modificationType.remove', 'Remove rule');
                                                        default: return t('messageWindow.modificationType.change', 'Change');
                                                    }
                                                };

                                                const getModificationDescription = (mod) => {
                                                    if (mod.reason) return mod.reason;
                                                    if (mod.description) return mod.description;
                                                    if (mod.summary) return mod.summary;
                                                    return t('messageWindow.noDescriptionAvailable', 'Description not available');
                                                };

                                                const getOriginalText = (mod) => {
                                                    if (mod.location && mod.location.original_text) return mod.location.original_text;
                                                    if (mod.before) return mod.before;
                                                    return null;
                                                };

                                                const getNewText = (mod) => {
                                                    if (mod.new_text) return mod.new_text;
                                                    if (mod.after) return mod.after;
                                                    if (mod.content) return mod.content;
                                                    return null;
                                                };
                                                
                                                return (
                                                    <div key={index} className={`modification-item ${selectedModifications.includes(index) ? 'selected' : ''}`}>
                                                        <div className="modification-header">
                                                            <label className="modification-checkbox">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedModifications.includes(index)}
                                                                    onChange={() => handleModificationToggle(index)}
                                                                />
                                                                <span className="modification-type">
                                                                    {getModificationTitle(modification)}
                                                                </span>
                                                            </label>
                                                        </div>
                                                        <div className="modification-content">
                                                            {/* Description */}
                                                            <p className="modification-description">
                                                                <strong>{t('messageWindow.description', 'What will change')}:</strong> 
                                                                {getModificationDescription(modification)}
                                                            </p>
                                                            
                                                            {/* Before content (original text) */}
                                                            {getOriginalText(modification) && (
                                                                <div className="modification-before">
                                                                    <strong>{t('messageWindow.currentText', 'Current text')}:</strong>
                                                                    <div className="code-block">{getOriginalText(modification)}</div>
                                                                </div>
                                                            )}
                                                            
                                                            {/* After content (new text) */}
                                                            {getNewText(modification) && (
                                                                <div className="modification-after">
                                                                    <strong>{t('messageWindow.newText', 'New text')}:</strong>
                                                                    <div className="code-block">{getNewText(modification)}</div>
                                                                </div>
                                                            )}
                                                            
                                                            {/* Show context if available */}
                                                            {modification.location && modification.location.context_before && (
                                                                <div className="modification-reasoning">
                                                                    <strong>{t('messageWindow.context', 'Context')}:</strong> 
                                                                    {t('messageWindow.contextDescription', 'This change will be made in the section that contains')}: 
                                                                    "...{modification.location.context_before.substring(0, 100)}..."
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                    
                                    <div className="selected-modifications-summary">
                                        <p>{t('messageWindow.selectedModificationsCount', 'Selected {{selected}} of {{total}} changes', {
                                            selected: selectedModifications.length,
                                            total: proposedModifications.length
                                        })}</p>
                                    </div>
                                </>
                            )}

                            {feedbackStep === 'applying' && (
                                <div className="applying-modifications">
                                    <div className="applying-spinner">
                                        <FaCircleNotch className="spinner" />
                                    </div>
                                    <p>{t('messageWindow.applyingModificationsMessage')}</p>
                                    <p className="applying-details">
                                        {t('messageWindow.applyingModificationsDetails', {
                                            count: selectedModifications.length,
                                            assistant: assistantInfo?.name
                                        })}
                                    </p>
                                </div>
                            )}
                        </div>
                        
                        <div className="feedback-modal-actions">
                            {feedbackStep === 'input' && (
                                <>
                                    <button 
                                        className="feedback-cancel-button" 
                                        onClick={closeFeedbackModal}
                                        disabled={submittingFeedback}
                                    >
                                        {t('messageWindow.cancel', 'Cancel')}
                                    </button>
                                    <button 
                                        className="feedback-submit-button" 
                                        onClick={handleSubmitFeedback}
                                        disabled={!feedbackContent.trim() || submittingFeedback}
                                    >
                                        {submittingFeedback ? (
                                            <>
                                                <FaCircleNotch className="spinner" /> {t('messageWindow.generatingPreview', 'Analyzing...')}
                                            </>
                                        ) : (
                                            <>
                                                <FaLightbulb /> {t('messageWindow.generatePreview', 'Analyze & Preview Changes')}
                                            </>
                                        )}
                                    </button>
                                </>
                            )}

                            {feedbackStep === 'preview' && (
                                <>
                                    <button 
                                        className="feedback-back-button" 
                                        onClick={handleBackToInput}
                                    >
                                        <FaArrowLeft /> {t('messageWindow.backToInput', 'Back to Edit')}
                                    </button>
                                    <button 
                                        className="feedback-apply-button" 
                                        onClick={handleApplyModifications}
                                        disabled={selectedModifications.length === 0}
                                    >
                                        <FaCheckCircle /> 
                                        {selectedModifications.length > 0 
                                            ? t('messageWindow.applySelectedChanges', 'Apply {{count}} Change(s)', { count: selectedModifications.length })
                                            : t('messageWindow.noChangesSelected', 'No Changes Selected')
                                        }
                                    </button>
                                </>
                            )}

                            {feedbackStep === 'applying' && (
                                <div className="applying-actions">
                                    <p>{t('messageWindow.pleaseWait', 'Please wait while changes are being applied...')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* File Overlay */}
            {selectedFile && (
                <div className="file-overlay">
                    <div className="file-overlay-content">
                        <button className="close-overlay" onClick={closeFileOverlay}>
                            <FaTimesCircle size={30} />
                        </button>
                        {selectedFile.mime_type === 'application/pdf' ? (
                            <object
                                data={selectedFile.url}
                                type="application/pdf"
                                width="100%"
                                height="100%"
                            >
                                <p>
                                    {t('messageWindow.unableToDisplayFile')}. <a href={selectedFile.url}> {t('messageWindow.downloadFile')}</a>
                                </p>
                            </object>
                        ) : (
                            <iframe 
                                src={selectedFile.url} 
                                title={selectedFile.name} 
                                width="100%" 
                                height="100%" 
                                frameBorder="0"
                            ></iframe>
                        )}
                    </div>
                </div>
            )}

            {/* Overlay for success/error messages */}
            {overlayState.show && (
                <div className="overlay">
                    <div className={`overlay-content ${overlayState.success ? 'success' : 'error'}`}>
                        <p>{overlayState.message}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MessageWindow;

